import type { StateAction } from "@entrilia/shared/types";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import type {
  AssistantAction,
  AssistantAttachment,
  AssistantChatHistory,
  AssistantChatUpdate,
  AssistantContentRef,
  AssistantFile,
  AssistantImage,
  AssistantRunEvents,
  AssistantRunHistory,
  AssistantRunStatus,
} from "../../../api/types/assistantTypes";
import { getContentKey } from "./content/assistantContent";

export type LastStepState = "Running" | "Interrupted";

export interface ChatStep {
  sequence: number;
  text: string;
}

export interface ChatReply {
  label: string;
  messageText: string;
}

export interface ChatItem {
  id: string;
  source: "user" | "assistant";
  isPending?: boolean;
  messageText?: string;
  images?: AssistantImage[];
  files?: AssistantFile[];
  contents?: AssistantContentRef[];
  statusText?: string;
  steps?: ChatStep[];
  lastStepState?: LastStepState;
  usedTools?: string[];
  actions?: AssistantAction[];
  replies?: ChatReply[];
}

export interface RunPreview {
  sequence: number;
  text: string;
}

export interface ChatState {
  runs: AssistantRunHistory[];
  updates: AssistantChatUpdate[];
  previews: Record<string, RunPreview[]>;
  isReadOnly: boolean;
}

interface AnswerPart {
  messageText: string;
  statusText?: string;
  steps: ChatStep[];
  usedTools?: string[];
  replies?: ChatReply[];
  images: AssistantImage[];
  files: AssistantFile[];
  contents: AssistantContentRef[];
  actions: AssistantAction[];
}

export const THINKING_TEXT = "Thinking…";
export const WORKING_TEXT = "Working…";

export const isRunFinished = (status: AssistantRunStatus): boolean =>
  status === "Completed" || status === "Failed" || status === "Stopped";

export const isRunPending = (status: AssistantRunStatus): boolean => status === "Queued" || status === "Running";

export const getLastSavedSequence = (run: AssistantRunHistory): number =>
  run.events.reduce((last, event) => Math.max(last, event.sequence), -1);

export const getUnfinishedRunIds = (state: ChatState | undefined): string[] =>
  (state?.runs ?? []).filter((run) => !isRunFinished(run.status)).map((run) => run.runId);

export const createChatState = (chat: AssistantChatHistory): ChatState => ({
  runs: chat.runs,
  updates: chat.updates,
  previews: {},
  isReadOnly: chat.isReadOnly,
});

const withRunPreviews = (
  previews: Record<string, RunPreview[]>,
  runId: string,
  runPreviews: RunPreview[]
): Record<string, RunPreview[]> => {
  const otherPreviews = Object.fromEntries(Object.entries(previews).filter(([previewRunId]) => previewRunId !== runId));
  return runPreviews.length > 0 ? { ...otherPreviews, [runId]: runPreviews } : otherPreviews;
};

const haveSameCards = (run: AssistantRunHistory, page: AssistantRunEvents): boolean =>
  run.cards.length === page.cards.length &&
  run.cards.every(
    (card, index) => card.callId === page.cards[index]?.callId && card.status === page.cards[index]?.status
  );

export const addQueuedRunAction =
  (runId: string, messageText: string, attachments: AssistantAttachment[]): StateAction<ChatState> =>
  (state) => {
    if (state.runs.some((existingRun) => existingRun.runId === runId)) {
      return state;
    }

    const run: AssistantRunHistory = {
      runId,
      status: "Queued",
      stopRequested: false,
      messageText,
      attachments: attachments.map((attachment) => ({
        id: attachment.attachmentId,
        name: attachment.fileName,
        size: attachment.size,
      })),
      events: [],
      cards: [],
    };
    return { ...state, runs: [...state.runs, run] };
  };

const keepUnsavedPreviews = (
  previews: Record<string, RunPreview[]>,
  runs: AssistantRunHistory[]
): Record<string, RunPreview[]> =>
  runs.reduce((kept, run) => {
    const lastSequence = getLastSavedSequence(run);
    const runPreviews = isRunFinished(run.status)
      ? []
      : (previews[run.runId] ?? []).filter((preview) => preview.sequence > lastSequence);
    return runPreviews.length > 0 ? { ...kept, [run.runId]: runPreviews } : kept;
  }, {});

export const mergeChatHistoryAction =
  (chat: AssistantChatHistory): StateAction<ChatState> =>
  (state) => {
    const mergedRuns = chat.runs.map((historyRun) => {
      const localRun = state.runs.find((run) => run.runId === historyRun.runId);
      return localRun && getLastSavedSequence(localRun) >= getLastSavedSequence(historyRun)
        ? { ...localRun, createdAt: historyRun.createdAt }
        : historyRun;
    });
    const localOnlyRuns = state.runs.filter(
      (localRun) => !chat.runs.some((historyRun) => historyRun.runId === localRun.runId)
    );
    const runs = [...mergedRuns, ...localOnlyRuns];
    return {
      runs,
      updates: chat.updates,
      previews: keepUnsavedPreviews(state.previews, runs),
      isReadOnly: chat.isReadOnly,
    };
  };

export const appendRunEventsAction =
  (page: AssistantRunEvents): StateAction<ChatState> =>
  (state) => {
    const run = state.runs.find((existingRun) => existingRun.runId === page.runId);
    if (!run) {
      return state;
    }

    const lastSequence = getLastSavedSequence(run);
    const newEvents = page.events.filter((event) => event.sequence > lastSequence);
    const isUnchanged =
      newEvents.length === 0 &&
      run.status === page.status &&
      run.failureText === page.failureText &&
      run.stopRequested === page.stopRequested &&
      haveSameCards(run, page);
    if (isUnchanged) {
      return state;
    }

    const updatedRun: AssistantRunHistory = {
      ...run,
      status: page.status,
      failureText: page.failureText,
      stopRequested: page.stopRequested,
      events: [...run.events, ...newEvents],
      cards: page.cards,
    };
    const updatedLastSequence = getLastSavedSequence(updatedRun);
    const unsavedPreviews = isRunFinished(page.status)
      ? []
      : (state.previews[run.runId] ?? []).filter((preview) => preview.sequence > updatedLastSequence);

    return {
      ...state,
      runs: state.runs.map((existingRun) => (existingRun === run ? updatedRun : existingRun)),
      previews: withRunPreviews(state.previews, run.runId, unsavedPreviews),
    };
  };

export const applyTextPreviewAction =
  (runId: string, preview: RunPreview): StateAction<ChatState> =>
  (state) => {
    const run = state.runs.find((existingRun) => existingRun.runId === runId);
    if (!run || isRunFinished(run.status) || preview.sequence <= getLastSavedSequence(run)) {
      return state;
    }

    const runPreviews = [
      ...(state.previews[runId] ?? []).filter((existingPreview) => existingPreview.sequence !== preview.sequence),
      preview,
    ].sort((first, second) => first.sequence - second.sequence);
    return { ...state, previews: withRunPreviews(state.previews, runId, runPreviews) };
  };

const createAnswerPart = (): AnswerPart => ({
  messageText: "",
  steps: [],
  images: [],
  files: [],
  contents: [],
  actions: [],
});

const splitIntoAnswerParts = (run: AssistantRunHistory, previewText: string): AnswerPart[] => {
  let part = createAnswerPart();
  const parts = [part];
  const startPartAfterCards = () => {
    if (part.actions.length > 0) {
      part = createAnswerPart();
      parts.push(part);
    }
  };

  for (const event of run.events) {
    if (event.type !== "Action" && event.type !== "ActionResolved" && event.type !== "ToolsUsed") {
      startPartAfterCards();
    }
    if (event.type !== "ToolsUsed") {
      part.statusText = undefined;
    }
    switch (event.type) {
      case "Text":
        part.messageText += event.text;
        break;
      case "Status":
        part.statusText = event.text;
        part.steps.push({ sequence: event.sequence, text: event.text });
        break;
      case "ToolsUsed":
        part.usedTools = event.names;
        break;
      case "Images":
        part.images.push(...event.images);
        break;
      case "Files":
        part.files.push(...event.files);
        break;
      case "Content":
        part.contents.push(...event.contents);
        break;
      case "Replies":
        part.replies = event.replies.map((reply) => ({ label: reply, messageText: reply }));
        break;
      case "Action":
        part.actions.push({
          ...event.action,
          status: run.cards.find((card) => card.callId === event.action.callId)?.status ?? "Pending",
        });
        break;
      case "ActionResolved":
        break;
    }
  }

  if (previewText || isRunPending(run.status) || run.status === "Failed") {
    startPartAfterCards();
  }
  if (previewText) {
    part.messageText += previewText;
    part.statusText = undefined;
  }
  return parts;
};

const appendFailureText = (text: string, failureText: string): string =>
  text ? `${text}\n\n${failureText}` : failureText;

const buildPendingStatusText = (part: AnswerPart): string | undefined =>
  part.statusText ?? (part.messageText ? WORKING_TEXT : THINKING_TEXT);

const getLastStepState = (part: AnswerPart, status: AssistantRunStatus): LastStepState | undefined => {
  if (part.statusText === undefined) {
    return undefined;
  }
  if (isRunPending(status)) {
    return "Running";
  }
  return status === "Failed" || status === "Stopped" ? "Interrupted" : undefined;
};

const isAnswerShown = (item: ChatItem): boolean =>
  !!item.isPending ||
  !!item.messageText ||
  !!item.steps ||
  !!item.images ||
  !!item.files ||
  !!item.contents ||
  !!item.actions ||
  !!item.replies?.length;

const buildAnswerItems = (run: AssistantRunHistory, previews: RunPreview[]): ChatItem[] => {
  const parts = splitIntoAnswerParts(run, previews.map((preview) => preview.text).join(""));
  const isPending = isRunPending(run.status);

  return parts
    .map((part, index): ChatItem => {
      const isLastPart = index === parts.length - 1;
      return {
        id: index === 0 ? run.runId : `${run.runId}-${index}`,
        source: "assistant",
        isPending: isLastPart && isPending,
        messageText:
          isLastPart && run.failureText ? appendFailureText(part.messageText, run.failureText) : part.messageText,
        statusText: isLastPart && isPending ? buildPendingStatusText(part) : undefined,
        steps: part.steps.length > 0 ? part.steps : undefined,
        lastStepState: isLastPart ? getLastStepState(part, run.status) : undefined,
        usedTools: part.usedTools,
        replies: isLastPart && run.status === "Failed" ? undefined : part.replies,
        images: part.images.length > 0 ? part.images : undefined,
        files: part.files.length > 0 ? part.files : undefined,
        contents: part.contents.length > 0 ? distinctBy(part.contents, getContentKey) : undefined,
        actions: part.actions.length > 0 ? part.actions : undefined,
      };
    })
    .filter(isAnswerShown);
};

const buildRunItems = (run: AssistantRunHistory, previews: RunPreview[]): ChatItem[] => {
  const answerItems = buildAnswerItems(run, previews);
  if (!run.messageText && run.attachments.length === 0) {
    return answerItems;
  }

  const userItem: ChatItem = {
    id: `${run.runId}-message`,
    source: "user",
    messageText: run.messageText,
    files: run.attachments.length > 0 ? run.attachments : undefined,
  };
  return [userItem, ...answerItems];
};

const buildUpdateItem = (update: AssistantChatUpdate): ChatItem => ({
  id: update.updateId,
  source: "assistant",
  messageText: update.text,
  replies:
    update.buttons.length > 0
      ? update.buttons.map((button) => ({ label: button.label, messageText: button.messageText }))
      : undefined,
});

const UNSAVED_RUN_TIME = Number.MAX_SAFE_INTEGER;

export const buildChatItems = (state: ChatState): ChatItem[] =>
  [
    ...state.runs.map((run) => ({
      time: run.createdAt ? Date.parse(run.createdAt) : UNSAVED_RUN_TIME,
      items: buildRunItems(run, state.previews[run.runId] ?? []),
    })),
    ...state.updates.map((update) => ({ time: Date.parse(update.createdAt), items: [buildUpdateItem(update)] })),
  ]
    .sort((first, second) => first.time - second.time)
    .flatMap((entry) => entry.items);
